import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import StepOne from "./StepOne";
import ParticipantsForm from "./ParticipantsForm";
import Summary from "./Summary";
import SuccessScreen from "./SuccessScreen";
import ErrorPopup from "./ErrorPopup";
import "../../css/RegistrationForm.css";
import api from "../../services/api";
import Loading from "../Loading";

const RegistrationForm = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    participants: [],
    additionalInfo: {
      departureCity: "",
      departureCityInfo: "",
      hasCar: false,
      canTakePassengers: false,
      passengerCount: null,
      hasPet: false,
      petInfo: "",
      userComment: "",
    },
    krInsurance: false,
    nnwInsurance: false,
    isTouristEvent: false,
  });

  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isResuming, setIsResuming] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).has("pr");
    } catch {
      return false;
    }
  });

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const pr = searchParams.get("pr");

  const buildParticipants = (count, email, phoneNumber) =>
    Array.from({ length: count }, (_, index) => ({
      id: index + 1,
      preferredName: "",
      email: index === 0 ? (email || "") : "",
      phone: index === 0 ? (phoneNumber || "") : "",
      isAdult: false,
      pronouns: "",
      firstTime: false,
    }));

  useEffect(() => {
    const run = async () => {
      if (!pr) {
        setIsResuming(false);
        return;
      }

      if (formData?.reservationGuid) {
        setIsResuming(false);
        return;
      }

      setIsResuming(true);

      try {
        const data = await api.resumePrereservation(pr);

        setFormData((prev) => ({
          ...prev,
          reservationGuid: data.reservationGuid,
          config: data.config,
          participants: buildParticipants(
            data.pax,
            data.email,
            data.phone
          ),
        }));

        setCurrentStep(2);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (err) {
        setErrorMessage(
          err?.response?.data?.message ||
            "Nie udało się wznowić prerezerwacji. Rozpocznij rejestrację ponownie."
        );

        setSearchParams((prevParams) => {
          const p = new URLSearchParams(prevParams);
          p.delete("pr");
          return p;
        }, { replace: true });

        setCurrentStep(1);
      } finally {
        setIsResuming(false);
      }
    };

    run();
  }, [pr, formData?.reservationGuid, setSearchParams]);

  useEffect(() => {
    const handlePopState = (e) => {
      if (currentStep === 3) {
        e.preventDefault();
        setCurrentStep(2);
      } else if (currentStep === 2) {
        const confirmExit = window.confirm(
          "Czy na pewno chcesz cofnąć się do formularza rejestracyjnego? Wprowadzone dane mogą zostać utracone."
        );
        if (!confirmExit) {
          navigate(0);
        } else {
          setSearchParams((prevParams) => {
            const p = new URLSearchParams(prevParams);
            p.delete("pr");
            return p;
          }, { replace: true });

          setCurrentStep(1);
        }
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [currentStep, navigate, setSearchParams]);

  useEffect(() => {
    window.history.replaceState({ step: 1 }, "", window.location.href);
  }, []);

  useEffect(() => {
    if (currentStep > 1) {
      window.history.pushState({ step: currentStep }, "", window.location.href);
    }
  }, [currentStep]);

  const handleNext = (data) => {
    if (currentStep === 1) {
      const participantsArr = buildParticipants(
        data.participants,
        data.email,
        data.phone
      );

      setFormData((prev) => ({
        ...prev,
        participants: participantsArr,
        reservationGuid: data.guid,
        config: data.config,
      }));

      setSearchParams((prevParams) => {
        const p = new URLSearchParams(prevParams);
        p.set("pr", data.guid);
        return p;
      }, { replace: true });
    } else {
      setFormData((prev) => ({ ...prev, ...data }));
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
    setCurrentStep((prev) => prev + 1);
  };

  const handleBack = (data) => {
    if (currentStep === 3) {
      setFormData((prev) => ({
        ...prev,
        participantsData: data.participantsData,
      }));
    }
    setCurrentStep((prev) => prev - 1);
  };

  const toNullableInt = (v) => {
    if (v === "" || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  };

  const handleSubmit = async (declarations, savedAddress) => {
    setIsLoading(true);
    try {
      const cleanedAdditionalInfo = {
        ...formData.additionalInfo,
        passengerCount: toNullableInt(formData.additionalInfo.passengerCount),
      };

      const payload = {
        additionalInfo: cleanedAdditionalInfo,
        krInsurance: formData.krInsurance,
        nnwInsurance: formData.nnwInsurance,
        participants: formData.participants,
        declarations,
        postalAddress: savedAddress || null,
      };

      await api.finishPrereservation(formData.reservationGuid, payload);

      setSearchParams((prevParams) => {
        const p = new URLSearchParams(prevParams);
        p.delete("pr");
        return p;
      }, { replace: true });

      setShowSuccess(true);
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message ||
          "Wystąpił błąd podczas kończenia rezerwacji."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      {isLoading || isResuming ? (
        <Loading />
      ) : showSuccess ? (
        <SuccessScreen />
      ) : (
        <div>
          {currentStep === 1 && <StepOne onNext={handleNext} />}

          {currentStep === 2 && (
            <ParticipantsForm
              data={formData}
              onNext={(data) => handleNext(data)}
              onBack={() => handleBack(formData)}
            />
          )}

          {currentStep === 3 && (
            <Summary
              data={formData}
              onConfirm={handleSubmit}
              onBack={() => handleBack(formData)}
            />
          )}
        </div>
      )}

      {errorMessage && (
        <ErrorPopup
          message={errorMessage}
          onClose={() => setErrorMessage("")}
        />
      )}

      <div style={{ height: "calc(80px + env(safe-area-inset-bottom))" }} />
    </div>
  );
};

export default RegistrationForm;